import React from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "components/SVG"
import { getVisiblePages } from "./utils"
import {
  paginationWrapper,
  paginationList,
  paginationItem,
  paginationButton,
  dotsStyles,
  desktopPagination,
  mobilePagination,
} from "./Pagination.css"

export type PaginationProps = {
  currentPage: number
  count: number
  query: string
  urlToPass?: string
  totalCount?: number
}

const dots = <li className={dotsStyles}>...</li>

const PaginationButton = ({ type, onClick, disabled, children, ariaLabel }) => (
  <button
    type="button"
    className={paginationButton[type]}
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
  >
    {children}
  </button>
)

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  count,
  query,
  urlToPass,
}) => {
  const showPagination = count > 1
  if (!showPagination) return null

  const firstPage = 1
  const firstDotsPage = 7
  const lastPage = count
  const previousPage = currentPage === firstPage ? null : currentPage - 1
  const nextPage = currentPage === count ? null : currentPage + 1

  const visiblePages = getVisiblePages(currentPage, count)
  const showDotsBeforeLastPage =
    count >= firstDotsPage && lastPage > visiblePages[3] + 1
  const showDotsAfterFirstPage = count >= firstDotsPage && visiblePages[0] > 2

  const firstPageUrl = urlToPass || `${query}${firstPage}`

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    page: number | null
  ) => {
    event.preventDefault()
    if (page !== null) {
      window.location.href = `${query}${page}`
    }
  }

  return (
    <nav
      className={paginationWrapper}
      role="navigation"
      aria-label="Pagination"
    >
      <PaginationButton
        type={previousPage ? "prev" : "prevDisabled"}
        onClick={(event) => handleClick(event, previousPage)}
        disabled={!previousPage}
        ariaLabel="Previous page"
      >
        <ChevronLeftIcon width="20" height="20" />
        <span>Prev</span>
      </PaginationButton>

      <div id="desktop-pagination" className={desktopPagination}>
        <ul className={paginationList}>
          <li className={paginationItem} key="first">
            <a
              href={firstPageUrl}
              aria-label={`Page ${firstPage}`}
              className={
                firstPage === currentPage
                  ? paginationButton.active
                  : paginationButton.default
              }
              aria-current={firstPage === currentPage ? "page" : undefined}
            >
              {firstPage}
            </a>
          </li>
          {showDotsAfterFirstPage && dots}
          {visiblePages.map((page) => (
            <li className={paginationItem} key={page}>
              <a
                href={`${query}${page}`}
                aria-label={`Page ${page}`}
                className={
                  page === currentPage
                    ? paginationButton.active
                    : paginationButton.default
                }
                aria-current={page === currentPage ? "page" : undefined}
              >
                {page}
              </a>
            </li>
          ))}
          {showDotsBeforeLastPage && dots}
          <li className={paginationItem} key="last">
            <a
              href={`${query}${lastPage}`}
              aria-label={`Page ${lastPage}`}
              className={
                lastPage === currentPage
                  ? paginationButton.active
                  : paginationButton.default
              }
              aria-current={lastPage === currentPage ? "page" : undefined}
            >
              {lastPage}
            </a>
          </li>
        </ul>
      </div>

      <div id="mobile-pagination" className={mobilePagination}>
        <p>
          Page {currentPage} of {lastPage}
        </p>
      </div>

      <PaginationButton
        type={nextPage ? "next" : "nextDisabled"}
        onClick={(event) => handleClick(event, nextPage)}
        disabled={!nextPage}
        ariaLabel="Next page"
      >
        <span>Next</span>
        <ChevronRightIcon />
      </PaginationButton>
    </nav>
  )
}
